import { useMemo } from "react";
import { SOLAR } from "@/constants/testIds";
import { computeSolar } from "@/lib/solar";
import { BROWSER_TZ, formatUtcOffset, timeZoneAt, zoneOffsetMinutes } from "@/lib/timezone";
import { useNow } from "@/hooks/useNow";
import { usePlace } from "@/hooks/usePlace";
import ClockSection from "@/components/solar/ClockSection";
import FormulaStrip from "@/components/solar/FormulaStrip";
import SunEvents from "@/components/solar/SunEvents";
import LocationPanel from "@/components/solar/LocationPanel";
import AnalemmaSection from "@/components/solar/AnalemmaSection";
import { DaylightSection, Explanation, GrainOverlay, PageFooter, PageHeader } from "@/components/solar/PageSections";

export default function SolarCalculator() {
  const now = useNow();
  const { place, setLocation, forget, leaveShared, initialLabel } = usePlace();
  const { lat, lon, label } = place;
  const approx = place.source === "guess";

  // Until the user picks a place, use their own clock's zone; afterwards, the zone of the place.
  const placeTz = useMemo(() => (approx ? BROWSER_TZ : timeZoneAt(lat, lon)), [approx, lat, lon]);
  const tzOffsetMinutes = zoneOffsetMinutes(now, placeTz);
  const tzLabel = formatUtcOffset(tzOffsetMinutes);
  const result = useMemo(() => computeSolar(now, lat, lon, tzOffsetMinutes), [now, lat, lon, tzOffsetMinutes]);

  return (
    <div
      data-testid={SOLAR.pageRoot}
      className="min-h-screen bg-[#f6efe3] font-serif text-[#2b1f14] selection:bg-[#c0501b] selection:text-[#f6efe3]"
    >
      <GrainOverlay />
      <div className="relative mx-auto max-w-5xl px-6 pb-24 pt-16 md:px-10 md:pt-24">
        <PageHeader />
        <ClockSection place={place} onForget={forget} onLeaveShared={leaveShared} result={result} now={now} placeTz={placeTz} tzLabel={tzLabel} />
        <FormulaStrip result={result} placeTz={placeTz} tzLabel={tzLabel} />
        <SunEvents result={result} placeTz={placeTz} now={now} lat={lat} />
        <LocationPanel lat={lat} lon={lon} tzOffsetMinutes={tzOffsetMinutes} initialQuery={initialLabel} onLocation={setLocation} />
        <DaylightSection now={now} lat={lat} placeLabel={label} />
        <AnalemmaSection now={now} />
        <Explanation />
        <PageFooter tzLabel={tzLabel} />
      </div>
    </div>
  );
}
